import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getTraineeClassrooms } from '../../../services/userService';
import debounce from 'lodash/debounce';
import { encodeData } from '../../../utils/encodeAndEncode';

const defaultProfile = '/images/profile/default-profile.png';

function AllClassroom() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [classrooms, setClassrooms] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loadingClassroom, setLoadingClassroom] = useState(null);
  const observer = useRef();
  const userId = JSON.parse(localStorage.getItem('user'))?.id;
  const isInitialMount = useRef(true);

  // Function to fetch classrooms
  const fetchClassrooms = async (pageNum, searchQuery, isNewSearch = false) => {
    try {
      setIsLoading(true);
      const response = await getTraineeClassrooms({
        user_id: userId,
        page: pageNum,
        limit: 10,
        search: searchQuery
      });

      if (response?.success && response?.data?.classrooms) {
        const newClassrooms = response.data.classrooms;
        const paginationInfo = response.data.pagination;

        // Remove duplicates from backend response
        const uniqueClassrooms = Array.from(
          new Map(newClassrooms.map(c => [c.id, c])).values()
        );

        if (isNewSearch) {
          setClassrooms(uniqueClassrooms);
        } else {
          setClassrooms(prevClassrooms => {
            const existingIds = new Set(prevClassrooms.map(c => c.id));
            const uniqueNew = uniqueClassrooms.filter(c => !existingIds.has(c.id));
            return [...prevClassrooms, ...uniqueNew];
          });
        }

        setPagination(paginationInfo);
        setHasMore(paginationInfo.currentPage < paginationInfo.totalPages);
      }
    } catch (error) {
      console.error('Error fetching classrooms:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((searchQuery) => {
      setPage(1);
      setClassrooms([]);
      fetchClassrooms(1, searchQuery, true);
    }, 500),
    [userId]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const searchQuery = e.target.value;
    setSearch(searchQuery);
    debouncedSearch(searchQuery);
  };

  // Last element ref callback for infinite scroll
  const lastClassroomElementRef = useCallback(node => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });

    if (node) observer.current.observe(node);
  }, [isLoading, hasMore]);

  // Initial load only
  useEffect(() => {
    fetchClassrooms(1, '', true);
  }, [userId]);

  // Pagination - load more
  useEffect(() => {
    if (page > 1) {
      fetchClassrooms(page, search, false);
    }
  }, [page]);

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { 
      year: 'numeric',
      month: 'short', 
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const handleClassroomClick = async (classroomId) => {
    try {
      setLoadingClassroom(classroomId);
      const encodedClassroomID = encodeData(classroomId);
      // Simulate loading for 2 seconds
      await new Promise(resolve => setTimeout(resolve, 2000));
      navigate(`/user/AllClassroom/classroomDetails/${encodedClassroomID}`);
    } catch (error) {
      console.error('Error navigating to classroom:', error);
    } finally {
      setLoadingClassroom(null);
    }
  };

  return (
    <div className="flex">
      <div className="w-full">
        {/* Search Bar */}
        <div className="grid grid-cols-12 gap-4 mb-4 mt-2">
          <div className="col-span-12 md:col-span-4 lg:col-span-4">
            <div>
              <input 
                type="text" 
                className="w-full px-3 py-2 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20" 
                placeholder="Search Live Sessions..."
                value={search}
                onChange={handleSearchChange}
                aria-label="Search Live Sessions"
              />
            </div>
          </div>
          {pagination && (
            <div className="col-span-12 md:col-span-8 lg:col-span-8 text-end">
              <small className="text-gray-500">
                Showing {classrooms.length} of {pagination.totalRecords} classrooms
              </small>
            </div>
          )}
        </div>

        {/* Classrooms Grid */}
<div className="grid grid-cols-12 gap-4">
  {Array.isArray(classrooms) && classrooms.map((classroom) => {
    // Safely extract values
    const cls_name = typeof classroom.cls_name === 'string' ? classroom.cls_name : 'Unnamed Classroom';
    const cls_desc = typeof classroom.cls_desc === 'string' ? classroom.cls_desc : 'No description available';
    const trainer_name = typeof classroom.trainer_name === 'string' ? classroom.trainer_name : 'Unknown Instructor';
    const total_users = typeof classroom.total_users === 'number' ? classroom.total_users : 0;
    const index = classrooms.indexOf(classroom);

    return (
      <div
        key={`classroom-${classroom.id}`}
        className="md:col-span-6 lg:col-span-4"
        ref={index === classrooms.length - 1 ? lastClassroomElementRef : null}
      >
        <div
          className="bg-white rounded-lg shadow-sm border border-gray-100 h-full"
          role="button"
          tabIndex={0}
        >
          <div className="relative">
            <img
              src={classroom.banner_img || defaultProfile}
              className="w-full h-48 object-cover rounded-t-lg"
              alt={cls_name}
              onError={(e) => {
                e.target.src = defaultProfile;
              }}
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center rounded-t-lg">
              <button
                className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg flex items-center gap-2"
                onClick={() => handleClassroomClick(classroom.id)}
                disabled={loadingClassroom === classroom.id}
              >
                <>
                  Enter Session
                  {loadingClassroom === classroom.id ? (
                    <span>
                      <span>.</span>
                      <span>.</span>
                      <span>.</span>
                    </span>
                  ) : (
                    <Icon icon="mdi:arrow-right" width="20" height="20" />
                  )}
                </>
              </button>
            </div>
          </div>
          <div className="p-4">
            <h5 className="font-semibold mb-3">{cls_name}</h5>
            <p className="mb-3">{cls_desc}</p>
            <div className="flex items-center mb-3">
              <img
                src={defaultProfile}
                className="rounded-full mr-2"
                alt={trainer_name}
                width="32"
                height="32"
              />
              <div>
                <small className="text-gray-500 block">Instructor</small>
                <span className="font-medium">{trainer_name}</span>
              </div>
            </div>
            <div className="flex flex-col md:flex-row justify-between items-center border-t pt-3">
              <div className="flex items-center justify-start w-full md:w-auto mb-2 md:mb-0">
                <Icon icon="mdi:account-group" className="text-gray-500" width="20" height="20" />
                <small className="text-gray-500 ml-1">{total_users} Students</small>
              </div>
              <div className="flex items-center justify-start w-full md:w-auto">
                <Icon icon="mdi:calendar-clock" className="text-gray-500" width="20" height="20" />
                <small className="text-gray-500 ml-1">
                  Joined: {formatDate(classroom.joined_at)}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  })}
</div>


        {/* Loading Indicator */}
        {isLoading && (
          <div className="text-center my-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}

        {/* No Results Message */}
        {!isLoading && (!classrooms || classrooms.length === 0) && (
          <div className="text-center my-4">
            <Icon icon="mdi:folder-open" width="48" height="48" className="text-gray-500 mb-2" />
            <h5>No classrooms found</h5>
            <p className="text-gray-500">Try adjusting your search criteria</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AllClassroom;